import { parseNavigationMarkers } from './utils/parseNavigationMarkers';
import NavigationSuggestion from './NavigationSuggestion';
import { ExternalLink } from 'lucide-react';

interface AssistantMessageContentProps {
  /** The raw message text (may contain [NAV:path|label] or [EXT:url|label] markers) */
  content: string;
  /** Whether this is an assistant message (navigation/external links only render in assistant messages) */
  isAssistant: boolean;
  onNavigate: (path: string) => void;
}

/**
 * Renders a single chat message, parsing any [NAV:path|label] markers
 * into clickable NavigationSuggestion components and [EXT:url|label]
 * markers into external links that open in a new tab.
 *
 * User messages are rendered as plain text (no parsing).
 */
const AssistantMessageContent = ({ content, isAssistant, onNavigate }: AssistantMessageContentProps) => {
  if (!isAssistant) {
    return <>{content}</>;
  }

  const segments = parseNavigationMarkers(content);

  if (segments.length === 1 && segments[0].type === 'text') {
    return <>{segments[0].content}</>;
  }

  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === 'text') {
          return <span key={idx}>{segment.content}</span>;
        }

        if (segment.type === 'navigation') {
          return (
            <NavigationSuggestion
              key={idx}
              path={segment.path}
              label={segment.label}
              onNavigate={onNavigate}
            />
          );
        }

        // segment.type === 'external_link'
        return (
          <a
            key={idx}
            href={segment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[13px] font-medium transition-all duration-150 hover:gap-1.5 cursor-pointer no-underline"
            style={{
              background: 'hsl(214 72% 38% / 0.08)',
              color: 'hsl(214 72% 38%)',
              border: '1px solid hsl(214 72% 38% / 0.15)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'hsl(214 72% 38% / 0.14)';
              e.currentTarget.style.borderColor = 'hsl(214 72% 38% / 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'hsl(214 72% 38% / 0.08)';
              e.currentTarget.style.borderColor = 'hsl(214 72% 38% / 0.15)';
            }}
          >
            <span>{segment.label}</span>
            <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        );
      })}
    </>
  );
};

export default AssistantMessageContent;